import { useState } from 'react';
import { X, Save, Plus, Check, AlertCircle } from 'lucide-react';
import { api, ApiError } from '../lib/api.js';
import { GlassButton, Badge, toast } from '../ui/index.js';
import { ProviderIcon } from '../ui/ProviderIcon.js';

export interface SourceProviderView {
  id: string; displayName: string; apiFormat: string; baseUrl: string;
  authHeader: string; authPrefix: string; extraHeaders: Record<string,string> | null;
  enabled: boolean; supportsCaching: boolean; supportsReasoningEffort: boolean;
  supportsByok: boolean; timeoutMs: number; primarySecretId: string | null;
  backupSecretId: string | null; createdAt: string; updatedAt: string;
  primarySecret: { secretId: string | null; secretName: string; configured: boolean; updatedAt: string | null };
  backupSecret: { secretId: string | null; secretName: string; configured: boolean; updatedAt: string | null };
}

const AVAILABLE_PROVIDERS = [
  { id: 'openrouter', displayName: 'OpenRouter', apiFormat: 'openai-compatible', baseUrl: 'https://openrouter.ai/api/v1' },
  { id: 'anthropic', displayName: 'Anthropic', apiFormat: 'anthropic', baseUrl: 'https://api.anthropic.com/v1' },
  { id: 'openai', displayName: 'OpenAI', apiFormat: 'openai-compatible', baseUrl: 'https://api.openai.com/v1' },
  { id: 'deepseek', displayName: 'DeepSeek', apiFormat: 'openai-compatible', baseUrl: 'https://api.deepseek.com' },
  { id: 'z-ai', displayName: 'Z.ai', apiFormat: 'openai-compatible', baseUrl: 'https://api.z.ai/api/paas/v4' },
  { id: 'x-ai', displayName: 'xAI (Grok)', apiFormat: 'openai-compatible', baseUrl: 'https://api.x.ai/v1' },
  { id: 'mistral', displayName: 'Mistral AI', apiFormat: 'openai-compatible', baseUrl: 'https://api.mistral.ai/v1' },
  { id: 'moonshot', displayName: 'Moonshot AI', apiFormat: 'openai-compatible', baseUrl: 'https://api.moonshot.cn/v1' },
  { id: 'google', displayName: 'Google Gemini', apiFormat: 'google', baseUrl: 'https://generativelanguage.googleapis.com/v1beta' },
];

interface Props {
  providers: SourceProviderView[];
  onClose: () => void;
  onSaved: () => void;
}

type PanelMode = 'list' | 'add' | 'edit';

export function SourceProviderPanel({ providers, onClose, onSaved }: Props) {
  const [mode, setMode] = useState<PanelMode>('list');
  const [editingProvider, setEditingProvider] = useState<SourceProviderView | null>(null);
  const [editPrimaryKey, setEditPrimaryKey] = useState('');
  const [editBackupKey, setEditBackupKey] = useState('');
  const [saving, setSaving] = useState(false);

  const configuredIds = new Set(providers.map(p => p.id));
  const availableToAdd = AVAILABLE_PROVIDERS.filter(p => !configuredIds.has(p.id));

  function openEdit(p: SourceProviderView) {
    setEditingProvider(p);
    setEditPrimaryKey('');
    setEditBackupKey('');
    setMode('edit');
  }

  async function addProvider(providerId: string) {
    const template = AVAILABLE_PROVIDERS.find(p => p.id === providerId);
    if (!template) return;
    setSaving(true);
    try {
      await api.put(`/api/v1/platform/source-providers/${encodeURIComponent(providerId)}`, {
        displayName: template.displayName,
        enabled: true,
      });
      toast.success(`${template.displayName} added. Configure the API key to activate it.`);
      onSaved();
      setMode('list');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Failed to add provider.');
    } finally { setSaving(false); }
  }

  async function save() {
    if (!editingProvider) return;
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {
        enabled: editingProvider.enabled,
        supportsByok: editingProvider.supportsByok,
        timeoutMs: editingProvider.timeoutMs,
      };
      if (editPrimaryKey) patch['primaryApiKey'] = editPrimaryKey;
      if (editBackupKey) patch['backupApiKey'] = editBackupKey;
      await api.put(`/api/v1/platform/source-providers/${encodeURIComponent(editingProvider.id)}`, patch);
      toast.success(`Provider "${editingProvider.displayName}" saved.`);
      setMode('list');
      setEditingProvider(null);
      onSaved();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Failed to save provider.');
    } finally { setSaving(false); }
  }

  const title = mode === 'add' ? 'Add Source Provider' : mode === 'edit' && editingProvider ? `Edit: ${editingProvider.displayName}` : 'Source Providers';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-[rgba(20,20,35,0.98)] border-l border-white/10 overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[rgba(20,20,35,0.98)] px-5 py-4">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <button onClick={mode !== 'list' ? () => setMode('list') : onClose} className="text-white/40 hover:text-white/60">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {mode === 'list' && (
            <>
              {providers.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-8 w-8 text-white/20 mx-auto mb-2" />
                  <p className="text-sm text-white/40">No providers configured yet.</p>
                  <p className="text-xs text-white/30 mt-1">Click "Add Provider" to configure your first source.</p>
                </div>
              ) : (
                providers.map(p => (
                  <div key={p.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:bg-white/[0.05] cursor-pointer transition"
                    onClick={() => openEdit(p)}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <ProviderIcon providerId={p.id} className="h-4 w-4 shrink-0 text-sky-300" />
                        <span className="text-sm font-semibold text-white/85">{p.displayName}</span>
                        <Badge>{p.apiFormat}</Badge>
                      </div>
                      <span className={`inline-block h-2 w-2 rounded-full ${p.primarySecret?.configured ? 'bg-emerald-400' : 'bg-amber-400'}`} title={p.primarySecret?.configured ? 'Key configured' : 'No key configured'} />
                    </div>
                    <div className="text-xs text-white/40 space-y-0.5">
                      <p className="flex items-center gap-1">
                        {p.primarySecret?.configured ? <Check className="h-3 w-3 text-emerald-400" /> : <AlertCircle className="h-3 w-3 text-amber-400" />}
                        Primary key: {p.primarySecret?.configured ? 'Configured' : 'Not configured'}
                      </p>
                      <p>Backup: {p.backupSecret?.configured ? 'Configured' : 'Not configured'}</p>
                      <p>Timeout: {(p.timeoutMs || 120000) / 1000}s · BYOK: {p.supportsByok ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                ))
              )}
              {availableToAdd.length > 0 && (
                <GlassButton variant="secondary" className="w-full" onClick={() => setMode('add')}>
                  <Plus className="h-4 w-4 mr-1" /> Add Provider
                </GlassButton>
              )}
            </>
          )}

          {mode === 'add' && (
            <>
              <p className="text-xs text-white/40">Select a provider to add. You'll configure the API key after adding.</p>
              {availableToAdd.map(p => (
                <div key={p.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:bg-white/[0.05] cursor-pointer transition"
                  onClick={() => void addProvider(p.id)}>
                  <div className="flex items-center gap-2 mb-1">
                    <ProviderIcon providerId={p.id} className="h-4 w-4 shrink-0 text-sky-300" />
                    <span className="text-sm font-semibold text-white/85">{p.displayName}</span>
                    <Badge>{p.apiFormat}</Badge>
                  </div>
                  <p className="text-xs text-white/40">{p.baseUrl}</p>
                </div>
              ))}
              {availableToAdd.length === 0 && (
                <p className="text-xs text-white/30 text-center py-4">All available providers are already configured.</p>
              )}
            </>
          )}

          {/* A form, so the key fields are not loose password inputs (Chrome warns about those),
              and Enter saves. The key fields say new-password: a form with a password field
              otherwise invites the browser to fill in the operator's own saved password, and a
              filled field would be saved as the provider's key. */}
          {mode === 'edit' && editingProvider && (
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void save(); }}>
              <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
                <div className="flex items-center gap-2">
                  <ProviderIcon providerId={editingProvider.id} className="h-4 w-4 shrink-0 text-sky-300" />
                  <p className="text-sm font-medium text-white/85">{editingProvider.displayName}</p>
                </div>
                <p className="text-xs text-white/40 mt-1">{editingProvider.id} · {editingProvider.apiFormat}</p>
                <p className="text-xs text-white/40 mt-0.5">{editingProvider.baseUrl}</p>
              </div>

              <label className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-white/85">Enabled</p>
                  <p className="mt-1 text-xs text-white/45">When disabled, models from this provider cannot be used.</p>
                </div>
                <input type="checkbox" className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400"
                  checked={editingProvider.enabled}
                  onChange={(e) => setEditingProvider(prev => prev ? { ...prev, enabled: e.target.checked } : null)} />
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Primary API Key</span>
                <input type="password" autoComplete="new-password" value={editPrimaryKey} onChange={(e) => setEditPrimaryKey(e.target.value)}
                  placeholder={editingProvider.primarySecret?.configured ? 'Leave blank to keep existing key' : 'Enter API key'}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50" />
                <p className="text-xs text-white/40">
                  {editingProvider.primarySecret?.configured
                    ? `Key configured. Leave blank to keep existing.`
                    : 'API key for this provider. Stored securely in Secret Hub.'}
                </p>
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Backup API Key (optional)</span>
                <input type="password" autoComplete="new-password" value={editBackupKey} onChange={(e) => setEditBackupKey(e.target.value)}
                  placeholder={editingProvider.backupSecret?.configured ? 'Leave blank to keep existing' : 'Enter backup API key'}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50" />
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">Timeout (ms)</span>
                <input type="number" step="1000" value={editingProvider.timeoutMs || 120000}
                  onChange={(e) => setEditingProvider(prev => prev ? { ...prev, timeoutMs: Number(e.target.value) } : null)}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/50" />
              </label>

              <label className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-white/85">Support BYOK</p>
                  <p className="mt-1 text-xs text-white/45">Allow orgs to use their own API key for models from this provider.</p>
                </div>
                <input type="checkbox" className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-400"
                  checked={editingProvider.supportsByok}
                  onChange={(e) => setEditingProvider(prev => prev ? { ...prev, supportsByok: e.target.checked } : null)} />
              </label>

              <GlassButton type="submit" variant="primary" loading={saving} className="w-full">
                <Save className="h-4 w-4 mr-1" /> Save Provider
              </GlassButton>

              <button type="button" onClick={() => setMode('list')} className="w-full text-xs text-white/30 hover:text-white/50 transition">
                ← Back to provider list
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
